import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Star, User, LogOut, Settings, Menu, X, Save, 
  Calendar as CalendarIcon, Shield, Gift, Users, Download, Share2, Copy, Globe, Mail, Upload
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format, parse } from "date-fns";
import { PushNotificationSettings } from "@/components/PushNotificationSettings";

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'es', name: 'Español', flag: '🇪🇸' }
];

const Profile = () => {
  const { user, logout, updateUser } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: "",
    date_of_birth: null,
    restaurant_consent: false,
    community_consent: false,
    email_notifications: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dobDate, setDobDate] = useState(null);

  const changeLanguage = async (langCode) => {
    i18n.changeLanguage(langCode);
    // Save to server for persistence
    try {
      const response = await axios.put(`${API}/users/profile`, {
        language: langCode
      }, { withCredentials: true });
      updateUser(response.data);
    } catch (error) {
      console.warn("Failed to save language preference:", error.message);
    }
  };

  // Load language from user profile on mount
  useEffect(() => {
    if (user?.language && user.language !== i18n.language) {
      i18n.changeLanguage(user.language);
    }
  }, [user?.language, i18n]);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "",
        date_of_birth: user.date_of_birth || null,
        restaurant_consent: user.restaurant_consent || false,
        community_consent: user.community_consent || false,
        email_notifications: user.email_notifications !== false
      });
      if (user.date_of_birth) {
        try {
          setDobDate(new Date(user.date_of_birth));
        } catch (e) {
          setDobDate(null);
        }
      }
      setLoading(false);
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/users/profile`, {
        name: profile.name,
        date_of_birth: dobDate ? format(dobDate, "yyyy-MM-dd") : null,
        restaurant_consent: profile.restaurant_consent,
        community_consent: profile.community_consent,
        email_notifications: profile.email_notifications
      }, { withCredentials: true });
      
      // Update both local state and global auth context
      const updatedUser = response.data;
      updateUser(updatedUser);
      setProfile({
        name: updatedUser.name || "",
        date_of_birth: updatedUser.date_of_birth || null,
        restaurant_consent: updatedUser.restaurant_consent || false,
        community_consent: updatedUser.community_consent || false,
        email_notifications: updatedUser.email_notifications !== false
      });
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleExportProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/profile/export`, { withCredentials: true });
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `etoile-profile-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Profile exported!");
    } catch (error) {
      toast.error("Failed to export profile");
    }
  };

  const handleShareProfile = () => {
    if (!user?.community_consent) {
      toast.error("Enable community participation to share your profile");
      return;
    }
    const shareUrl = `${window.location.origin}/profile/share/${user.user_id}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success("Profile link copied to clipboard!");
  };

  const handleImportVisits = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Extract visits from exported data
      const visits = data.visits?.map(v => ({
        restaurant_name: v.restaurant_name || v.restaurant?.name,
        stars: v.stars,
        visit_date: v.visit_date,
        visit_count: v.visit_count || 1,
        notes: v.notes,
        cost: v.cost,
        currency: v.currency || "EUR"
      })).filter(v => v.restaurant_name && v.visit_date);
      
      if (!visits || visits.length === 0) {
        toast.error("Keine gültigen Besuche in der Datei gefunden");
        return;
      }
      
      const response = await axios.post(`${API}/users/profile/import`, { visits }, { withCredentials: true });
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} Besuche importiert!`);
        // Reload page to update stats
        window.location.reload();
      } else {
        toast.info(`Keine neuen Besuche importiert (${response.data.skipped} übersprungen)`);
      }
      
      if (response.data.errors?.length > 0) {
        console.warn("Import errors:", response.data.errors);
      }
    } catch (error) {
      toast.error("Import fehlgeschlagen - ungültiges Dateiformat");
    }
    
    // Reset file input
    e.target.value = '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      {/* Navigation */}
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <Star className="w-5 h-5 text-primary fill-primary" />
              <span className="font-semibold gradient-text">Étoile</span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => navigate('/dashboard')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.dashboard')}</button>
              <button onClick={() => navigate('/restaurants')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.restaurants')}</button>
              <button onClick={() => navigate('/preferences')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.preferences')}</button>
              <button onClick={() => navigate('/leaderboard')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.leaderboard')}</button>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 h-10">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-popover border-border">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />{t('common.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-muted/50" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />{t('common.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border">
              <div className="flex flex-col gap-2">
                <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.dashboard')}</button>
                <button onClick={() => { navigate('/restaurants'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.restaurants')}</button>
                <button onClick={() => { navigate('/preferences'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.preferences')}</button>
                <button onClick={() => { navigate('/leaderboard'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.leaderboard')}</button>
                <button onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} className="text-left py-2 text-foreground">Profile</button>
                <button onClick={handleLogout} className="text-left py-2 text-destructive">Logout</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">{t('profile.title')}</h1>
            <p className="text-muted-foreground">{t('profile.subtitle')}</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Profile Picture and Export */}
          <Card className="bg-card border-border" data-testid="profile-picture-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-20 h-20 rounded-full" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-10 h-10 text-primary" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-lg">{user?.name}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleExportProfile}
                    className="border-border hover:border-primary"
                    data-testid="export-profile-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('profile.export')}
                  </Button>
                  <label>
                    <input 
                      type="file" 
                      accept=".json"
                      onChange={handleImportVisits}
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-border hover:border-primary cursor-pointer"
                      data-testid="import-profile-button"
                      asChild
                    >
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Import
                      </span>
                    </Button>
                  </label>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleShareProfile}
                    className="border-border hover:border-primary"
                    data-testid="share-profile-button"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    {t('profile.share')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card className="bg-card border-border" data-testid="basic-info-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                {t('profile.basicInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">{t('profile.fullName')}</Label>
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="bg-transparent border-border focus:border-primary"
                  data-testid="profile-name-input"
                />
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">{t('profile.dateOfBirth')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start border-border hover:border-primary"
                      data-testid="dob-button"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {dobDate ? format(dobDate, "PPP") : t('profile.selectDob')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-popover border-border">
                    <Calendar
                      mode="single"
                      selected={dobDate}
                      onSelect={setDobDate}
                      captionLayout="dropdown-buttons"
                      fromYear={1920}
                      toYear={new Date().getFullYear()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </CardContent>
          </Card>

          {/* Restaurant Consent */}
          <Card className="bg-card border-border" data-testid="consent-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                {t('profile.restaurantSharing')}
              </CardTitle>
              <CardDescription>
                {t('profile.restaurantSharingDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-muted/20 rounded">
                <div className="flex items-center gap-3">
                  <Gift className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium">{t('profile.shareWithRestaurants')}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('profile.shareWithRestaurantsDesc')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={profile.restaurant_consent}
                  onCheckedChange={(checked) => setProfile({ ...profile, restaurant_consent: checked })}
                  data-testid="consent-switch"
                />
              </div>
              
              {profile.restaurant_consent && (
                <div className="mt-4 p-4 border border-primary/20 rounded bg-primary/5">
                  <p className="text-sm text-primary">
                    {t('profile.sharingEnabled')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Community Consent */}
          <Card className="bg-card border-border" data-testid="community-consent-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                {t('profile.communityParticipation')}
              </CardTitle>
              <CardDescription>
                {t('profile.communityParticipationDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-muted/20 rounded">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium">{t('profile.joinCommunity')}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('profile.joinCommunityDesc')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={profile.community_consent}
                  onCheckedChange={(checked) => setProfile({ ...profile, community_consent: checked })}
                  data-testid="community-consent-switch"
                />
              </div>
              
              {profile.community_consent && (
                <div className="mt-4 p-4 border border-primary/20 rounded bg-primary/5">
                  <p className="text-sm text-primary">
                    {t('profile.communityEnabled')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Language Settings */}
          <Card className="bg-card border-border" data-testid="language-settings-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                {t('profile.language')}
              </CardTitle>
              <CardDescription>
                {t('profile.languageDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={i18n.language} onValueChange={changeLanguage}>
                <SelectTrigger className="w-full bg-transparent border-border" data-testid="language-select">
                  <SelectValue>
                    {LANGUAGES.find(l => l.code === i18n.language)?.flag} {LANGUAGES.find(l => l.code === i18n.language)?.name || 'English'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {LANGUAGES.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      <span className="flex items-center gap-2">
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Push Notifications */}
          <PushNotificationSettings />

          {/* Email Notifications */}
          <Card className="bg-card border-border" data-testid="email-notifications-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                {t('profile.emailNotifications', 'Email Notifications')}
              </CardTitle>
              <CardDescription>
                {t('profile.emailNotificationsDesc', 'Receive emails for achievements and badge upgrades')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-muted/20 rounded">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium">
                      {profile.email_notifications ? t('profile.emailsEnabled', 'Emails Enabled') : t('profile.emailsDisabled', 'Emails Disabled')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {profile.email_notifications 
                        ? t('profile.emailsEnabledDesc', 'You will receive achievement and badge emails')
                        : t('profile.emailsDisabledDesc', 'No email notifications will be sent')
                      }
                    </p>
                  </div>
                </div>
                <Switch
                  checked={profile.email_notifications}
                  onCheckedChange={(checked) => setProfile({ ...profile, email_notifications: checked })}
                  data-testid="email-notifications-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="save-profile-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? t('profile.saving') : t('profile.saveChanges')}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Profile;
